import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Trash2, Inbox, Phone, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";
import { LOGO_URL } from "@/data/catalog";

const STATUSES = [
  { id: "pending_review", label: "Pending" },
  { id: "contacted", label: "Contacted" },
  { id: "completed", label: "Completed" },
];

const fmtError = (d) => {
  if (d == null) return "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(d);
};

const money = (n) => `$${Math.round(n || 0).toLocaleString()}`;

const specLines = (q) => {
  const lines = [];
  if (q.wrap_specifications) {
    const w = q.wrap_specifications;
    lines.push(`${w.finish} · ${w.color_name} (${w.color_hex})`);
    if (w.accents?.length) lines.push(`Accents: ${w.accents.join(", ")}`);
    if (w.overlay_text) lines.push(`Lettering: "${w.overlay_text}"`);
    lines.push(`Estimate: ${money(w.estimated_price)}`);
  }
  if (q.custom_decal_specs) {
    const d = q.custom_decal_specs;
    lines.push(`${d.shape} · "${d.text}" · ${d.style} · ${d.color} · ${d.size_inches}"`);
    lines.push(`Estimate: ${money(d.estimated_price)}`);
  }
  return lines;
};

const Admin = () => {
  const [user, setUser] = useState(undefined);
  const [quotes, setQuotes] = useState([]);
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/quotes`, { withCredentials: true });
    setQuotes(data);
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => {
        setUser(r.data);
        load().catch(() => {});
      })
      .catch(() => setUser(null));
  }, [load]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, form, { withCredentials: true });
      setUser(data);
      await load();
      toast.success("Welcome back");
    } catch (err) {
      setError(fmtError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(null);
  };

  const setStatus = async (id, status) => {
    await axios.patch(`${API}/quotes/${id}`, { status }, { withCredentials: true });
    setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
  };

  const remove = async (id) => {
    await axios.delete(`${API}/quotes/${id}`, { withCredentials: true });
    setQuotes((qs) => qs.filter((q) => q.id !== id));
    toast.success("Quote deleted");
  };

  if (user === undefined)
    return (
      <main className="pt-24 pb-20 min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-velocity" />
      </main>
    );

  if (user === null)
    return (
      <main data-testid="admin-login-view" className="pt-24 pb-20 min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-sm rounded-2xl border border-edge bg-panel p-8">
          <img src={LOGO_URL} alt="G5" className="h-14 w-14 rounded-full ring-2 ring-velocity/70 mx-auto" />
          <h1 className="mt-4 text-center font-display font-black text-2xl uppercase tracking-tight">Owner Login</h1>
          <p className="mt-1 text-center text-xs text-slate-500 font-mono tracking-widest uppercase">Quote dashboard access</p>
          <form data-testid="admin-login-form" onSubmit={login} className="mt-6 space-y-3">
            <Input
              data-testid="admin-email-input"
              type="email"
              required
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="bg-gunmetal border-edge"
            />
            <Input
              data-testid="admin-password-input"
              type="password"
              required
              placeholder="Password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              className="bg-gunmetal border-edge"
            />
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-velocity">{error}</p>
            )}
            <Button data-testid="admin-login-submit-btn" type="submit" disabled={busy} className="w-full bg-velocity hover:bg-velocitydark text-white font-semibold">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
            </Button>
          </form>
        </div>
      </main>
    );

  const pending = quotes.filter((q) => q.status === "pending_review").length;

  return (
    <main data-testid="admin-dashboard" className="pt-24 pb-20 min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-mono text-xs tracking-[0.25em] text-velocity uppercase font-bold">Owner Dashboard</p>
            <h1 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl mt-1">Quote Requests</h1>
            <p className="text-sm text-slate-400 mt-1">
              {quotes.length} total · <span className="text-velocity">{pending} pending review</span>
            </p>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-edge text-slate-300 text-sm hover:border-velocity/60 hover:text-white transition-colors"
          >
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
        </div>

        {quotes.length === 0 ? (
          <div className="mt-16 text-center text-slate-500">
            <Inbox className="h-10 w-10 mx-auto mb-3 text-steel" />
            No quote requests yet. New submissions from the website land here instantly.
          </div>
        ) : (
          <div className="mt-8 space-y-4">
            {quotes.map((q) => (
              <div key={q.id} data-testid={`quote-card-${q.id}`} className="rounded-2xl border border-edge bg-panel p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-display font-bold text-lg tracking-tight">{q.client_name}</p>
                    <div className="flex flex-wrap gap-4 mt-1 text-sm text-slate-400">
                      <a href={`mailto:${q.email}`} className="inline-flex items-center gap-1.5 hover:text-white"><Mail className="h-3.5 w-3.5 text-velocity" />{q.email}</a>
                      {q.phone && (
                        <a href={`tel:${q.phone}`} className="inline-flex items-center gap-1.5 hover:text-white"><Phone className="h-3.5 w-3.5 text-velocity" />{q.phone}</a>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {STATUSES.map((s) => (
                      <button
                        key={s.id}
                        data-testid={`quote-status-${s.id}-${q.id}`}
                        onClick={() => setStatus(q.id, s.id)}
                        className={`px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider border transition-colors ${
                          q.status === s.id
                            ? "bg-velocity border-velocity text-white"
                            : "border-edge text-slate-400 hover:text-white hover:border-steel"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                    <button
                      data-testid={`quote-delete-${q.id}`}
                      onClick={() => remove(q.id)}
                      className="p-2 rounded-full border border-edge text-slate-500 hover:text-velocity hover:border-velocity/60 transition-colors"
                      aria-label="Delete quote"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {q.has_build_image && (
                  <img
                    src={`${API}/builds/${q.id}.jpg`}
                    alt="Customer build"
                    data-testid={`quote-build-img-${q.id}`}
                    className="mt-4 rounded-lg border border-edge w-full max-w-sm"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                )}
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 font-mono text-xs text-slate-300">
                  <p><span className="text-steel">SOURCE</span> — {q.source.replace(/_/g, " ")}</p>
                  {q.service && <p><span className="text-steel">SERVICE</span> — {q.service}</p>}
                  {(q.vehicle_info?.year_make_model || q.vehicle_info?.vehicle_type) && (
                    <p><span className="text-steel">VEHICLE</span> — {q.vehicle_info.year_make_model || q.vehicle_info.vehicle_type}</p>
                  )}
                  {q.timeline && <p><span className="text-steel">TIMELINE</span> — {q.timeline}</p>}
                  {q.budget && <p><span className="text-steel">BUDGET</span> — {q.budget}</p>}
                  {specLines(q).map((l, i) => (
                    <p key={i}><span className="text-steel">SPEC</span> — {l}</p>
                  ))}
                  {q.notes && <p className="sm:col-span-2"><span className="text-steel">NOTES</span> — {q.notes}</p>}
                  <p><span className="text-steel">RECEIVED</span> — {new Date(q.created_at).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

export default Admin;
